import { IStorageService } from '../interfaces/IStorageService';
import logger from '../loggerService';

/**
 * Web Implementation of Storage Service using localStorage
 * Enterprise-ready with error handling and robust serialization.
 */
export class WebStorageService implements IStorageService {
    async saveWithExpiry<T>(key: string, value: T, ttlMs?: number | null): Promise<void> {
        try {
            const expiry = ttlMs ? Date.now() + ttlMs : null;
            const payload = { value, expiry };
            const serializedValue = JSON.stringify(payload);
            localStorage.setItem(key, serializedValue);
        } catch (error) {
            logger.error(`Failed to save to localStorage with expiry [${key}]`, error, 'WebStorageService');
            throw error;
        }
    }

    async save<T>(key: string, value: T): Promise<void> {
        return this.saveWithExpiry(key, value, null);
    }

    async get<T>(key: string): Promise<T | null> {
        try {
            const serializedValue = localStorage.getItem(key);
            if (serializedValue === null) return null;
            
            const payload = JSON.parse(serializedValue);
            if (payload && typeof payload === 'object' && 'value' in payload && 'expiry' in payload) {
                const expiry = payload.expiry;
                if (expiry !== null && typeof expiry === 'number' && Date.now() > expiry) {
                    localStorage.removeItem(key);
                    return null;
                }
                return payload.value as T;
            }
            return payload as T;
        } catch (error) {
            logger.warn(`Failed to parse localStorage value [${key}]`, { error, context: 'WebStorageService' });
            return null;
        }
    }

    async remove(key: string): Promise<void> {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            logger.error(`Failed to remove from localStorage [${key}]`, error, 'WebStorageService');
        }
    }

    async clear(): Promise<void> {
        try {
            localStorage.clear();
        } catch (error) {
            logger.error('Failed to clear localStorage', error, 'WebStorageService');
        }
    }
}
